import type { MixDecision, VoiceDecision } from '@sw/engine'
import { dbToLinear } from '@sw/engine'
import type { Emitter, SoundSource, WorldDefinition } from '@sw/schema'

export interface LoadedAsset {
  id: string
  buffer?: AudioBuffer
  /** Long files stream from an element rather than being decoded whole. */
  url: string
  bytes: number
}

/** Anything longer than this streams instead of being decoded into memory. */
const STREAM_THRESHOLD_BYTES = 3 * 1024 * 1024

interface Voice {
  emitterId: string
  source: AudioBufferSourceNode | MediaElementAudioSourceNode
  element?: HTMLAudioElement
  /** The authored fade in and out. Separate from the mix's own gain, which is
   *  recomputed every frame and would trample a ramp. */
  envelope: GainNode
  gain: GainNode
  pannedGain: GainNode
  directGain: GainNode
  panner: PannerNode
  startedAt: number
  loop: boolean
  onEnded?: () => void
}

export interface RuntimeCallbacks {
  onEmitterEnded?: (emitterId: string) => void
  onGlitch?: () => void
}

/**
 * The Web Audio side of the player.
 *
 * It owns no opinions about what should be heard — `@sw/engine` decides that
 * and hands over gains and directions. This class only builds the graph and
 * moves the numbers onto it, which is why the interesting half of the audio
 * system can be tested without a browser.
 *
 * Each voice runs down two parallel paths: one through an HRTF panner for
 * sources with a direction, one straight through for layers that surround the
 * listener. Crossfading between them is what makes stepping into a courtyard
 * feel like arriving rather than like a switch being thrown.
 */
export class AudioRuntime {
  private context: AudioContext | undefined
  private master: GainNode | undefined
  private limiter: DynamicsCompressorNode | undefined
  private readonly buses = new Map<string, GainNode>()
  private readonly voices = new Map<string, Voice>()
  private readonly assets = new Map<string, LoadedAsset>()
  private readonly emitters = new Map<string, Emitter>()
  private readonly playlistCursor = new Map<string, number>()
  private readonly seamlessBuffers = new Map<string, AudioBuffer>()
  private callbacks: RuntimeCallbacks = {}

  /** Blob URLs for streamed assets, revoked on dispose. */
  private readonly objectUrls: string[] = []

  get running(): boolean {
    return this.context?.state === 'running'
  }

  /**
   * Replaces the callbacks. The session runtime needs to hear about a voice
   * ending — the audio context is created first, inside the tap that started
   * it, so it cannot be passed in at construction.
   */
  setCallbacks(callbacks: RuntimeCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks }
  }

  get sampleRate(): number {
    return this.context?.sampleRate ?? 0
  }

  /**
   * Must be called from a real tap. Browsers refuse to start audio otherwise,
   * which is the whole reason the player opens with a deliberate "start
   * listening" step rather than trying to fade in on arrival.
   */
  async start(callbacks: RuntimeCallbacks = {}): Promise<void> {
    this.callbacks = callbacks
    if (!this.context) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      this.context = new Ctor({ latencyHint: 'interactive' })

      // A brickwall at the end of the chain. A pile-up of triggers must never
      // become a shout into somebody's ears.
      this.limiter = this.context.createDynamicsCompressor()
      this.limiter.threshold.value = -1
      this.limiter.knee.value = 0
      this.limiter.ratio.value = 20
      this.limiter.attack.value = 0.003
      this.limiter.release.value = 0.25

      this.master = this.context.createGain()
      this.master.gain.value = 1
      this.master.connect(this.limiter)
      this.limiter.connect(this.context.destination)

      // The listener stays at the origin facing forward. Direction is applied
      // by placing each source at a unit distance on the bearing the engine
      // worked out, so the panner never re-does the distance maths.
      const listener = this.context.listener
      if (listener.forwardZ) {
        listener.forwardX.value = 0
        listener.forwardY.value = 0
        listener.forwardZ.value = -1
        listener.upX.value = 0
        listener.upY.value = 1
        listener.upZ.value = 0
      }
    }

    if (this.context.state === 'suspended') await this.context.resume()
  }

  async suspend(): Promise<void> {
    if (this.context?.state === 'running') await this.context.suspend()
  }

  /**
   * Renders a live microphone as an ordinary voice.
   *
   * Routed through the same chain as everything else, so a voice arriving from
   * across the world gets the same distance, direction and ducking as a sound
   * that was authored into the ground — which is the whole point of putting
   * somebody at a spot on a map rather than simply unmuting them.
   *
   * A silent element is kept alongside it because some browsers will not pull
   * samples through a WebRTC stream until it is attached to one.
   */
  playStream(emitterId: string, stream: MediaStream, busId: string): void {
    if (!this.context || !this.master) return
    this.stop(emitterId, 0)

    const bus = this.buses.get(busId) ?? this.master
    const envelope = this.context.createGain()
    const gain = this.context.createGain()
    const pannedGain = this.context.createGain()
    const directGain = this.context.createGain()
    const panner = this.context.createPanner()

    panner.panningModel = 'HRTF'
    panner.distanceModel = 'inverse'
    panner.refDistance = 1
    panner.maxDistance = 10_000
    panner.rolloffFactor = 0
    panner.positionZ.value = -1

    gain.gain.value = 0
    pannedGain.gain.value = 1
    directGain.gain.value = 0

    envelope.connect(gain)
    gain.connect(pannedGain).connect(panner).connect(bus)
    gain.connect(directGain).connect(bus)

    const source = this.context.createMediaStreamSource(stream)
    source.connect(envelope)

    const keepAlive = new Audio()
    keepAlive.srcObject = stream
    keepAlive.muted = true
    void keepAlive.play().catch(() => undefined)

    this.voices.set(emitterId, {
      emitterId,
      source: source as unknown as MediaElementAudioSourceNode,
      element: keepAlive,
      envelope,
      gain,
      pannedGain,
      directGain,
      panner,
      startedAt: this.context.currentTime,
      loop: true,
    })
  }

  /** Adds an emitter the world did not ship with, such as a spawned one-shot. */
  registerEmitter(emitter: Emitter): void {
    this.emitters.set(emitter.id, emitter)
  }

  setWorld(definition: WorldDefinition): void {
    if (!this.context || !this.master) throw new Error('Start the audio runtime first.')

    this.emitters.clear()
    for (const emitter of definition.emitters) this.emitters.set(emitter.id, emitter)

    for (const bus of definition.buses) {
      if (this.buses.has(bus.id)) continue
      const gain = this.context.createGain()
      gain.gain.value = dbToLinear(bus.gainDb)
      gain.connect(this.master)
      this.buses.set(bus.id, gain)
    }
  }

  /** Fetches every asset the world needs before the listener sets off. */
  async loadAssets(
    assets: { id: string; url: string; bytes: number }[],
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<void> {
    if (!this.context) throw new Error('Start the audio runtime first.')

    let loaded = 0
    for (const asset of assets) {
      if (!this.assets.has(asset.id)) {
        const response = await fetch(asset.url, { headers: authHeader() })
        if (!response.ok) throw new Error(`Could not fetch audio ${asset.id}`)

        if (asset.bytes > STREAM_THRESHOLD_BYTES) {
          // Long narration is played through a media element rather than
          // decoded, which would hold tens of megabytes of PCM for something
          // played once. It has to be fetched here and handed over as a blob:
          // a media element cannot send an Authorization header, and the audio
          // endpoint requires one, so pointing it straight at the URL simply
          // 401s. (A short-lived signed URL would let it stream properly; that
          // is the upgrade when long narration becomes common.)
          const objectUrl = URL.createObjectURL(await response.blob())
          this.objectUrls.push(objectUrl)
          this.assets.set(asset.id, { id: asset.id, url: objectUrl, bytes: asset.bytes })
        } else {
          const buffer = await this.context.decodeAudioData(await response.arrayBuffer())
          this.assets.set(asset.id, { id: asset.id, url: asset.url, bytes: asset.bytes, buffer })
        }
      }
      loaded += 1
      onProgress?.(loaded, assets.length)
    }
  }

  play(emitterId: string, options: { fromOffsetMs?: number } = {}): void {
    const emitter = this.emitters.get(emitterId)
    if (!emitter || !this.context) return

    this.stop(emitterId, 0)

    const assetId = this.pickAsset(emitter.id, emitter.source)
    if (!assetId) return
    const asset = this.assets.get(assetId)
    if (!asset) return

    const bus = this.buses.get(emitter.busId) ?? this.master
    if (!bus) return

    const envelope = this.context.createGain()
    const gain = this.context.createGain()
    const pannedGain = this.context.createGain()
    const directGain = this.context.createGain()
    const panner = this.context.createPanner()

    panner.panningModel = 'HRTF'
    panner.distanceModel = 'inverse'
    panner.refDistance = 1
    panner.maxDistance = 10_000
    panner.rolloffFactor = 0
    panner.positionZ.value = -1

    // Start silent and let the first mix decision bring it in, so nothing
    // ever arrives at full volume for a frame.
    gain.gain.value = 0
    pannedGain.gain.value = 1
    directGain.gain.value = 0

    envelope.connect(gain)
    gain.connect(pannedGain).connect(panner).connect(bus)
    gain.connect(directGain).connect(bus)

    // Authored fade-in: the sound arrives rather than appearing.
    const fadeInSeconds = emitter.fadeIn.durationMs / 1000
    if (fadeInSeconds > 0) {
      envelope.gain.setValueAtTime(0, this.context.currentTime)
      envelope.gain.linearRampToValueAtTime(1, this.context.currentTime + fadeInSeconds)
    }

    let source: AudioBufferSourceNode | MediaElementAudioSourceNode
    let element: HTMLAudioElement | undefined

    if (asset.buffer) {
      const bufferSource = this.context.createBufferSource()
      // A loop whose ends do not match clicks once a bar, which is worse than
      // no ambience at all. The seam is crossfaded into the buffer once and
      // cached, so plain looping is then genuinely seamless.
      bufferSource.buffer =
        emitter.loop && emitter.loopCrossfadeMs > 0
          ? this.seamless(assetId, asset.buffer, emitter.loopCrossfadeMs)
          : asset.buffer
      bufferSource.loop = emitter.loop
      bufferSource.connect(envelope)
      bufferSource.start(0, (options.fromOffsetMs ?? 0) / 1000)
      source = bufferSource
    } else {
      element = new Audio(asset.url)
      element.crossOrigin = 'use-credentials'
      element.loop = emitter.loop
      element.currentTime = (options.fromOffsetMs ?? 0) / 1000
      const media = this.context.createMediaElementSource(element)
      media.connect(envelope)
      void element.play().catch(() => this.callbacks.onGlitch?.())
      source = media
    }

    const voice: Voice = {
      emitterId,
      source,
      element,
      gain,
      pannedGain,
      directGain,
      panner,
      envelope,
      startedAt: this.context.currentTime,
      loop: emitter.loop,
    }

    const ended = (): void => {
      if (this.voices.get(emitterId) === voice) {
        this.voices.delete(emitterId)
        this.callbacks.onEmitterEnded?.(emitterId)
      }
    }
    voice.onEnded = ended
    if (asset.buffer) (source as AudioBufferSourceNode).onended = ended
    else element?.addEventListener('ended', ended)

    this.voices.set(emitterId, voice)
  }

  stop(emitterId: string, fadeMs?: number): void {
    const voice = this.voices.get(emitterId)
    if (!voice || !this.context) return

    // An explicit fade from a rule wins; otherwise the emitter's own fade-out
    // applies, which is what the author set it for.
    const authored = this.emitters.get(emitterId)?.fadeOut.durationMs ?? 0
    const fade = fadeMs ?? authored

    this.voices.delete(emitterId)
    const now = this.context.currentTime

    if (fade > 0) {
      voice.envelope.gain.cancelScheduledValues(now)
      voice.envelope.gain.setValueAtTime(voice.envelope.gain.value, now)
      voice.envelope.gain.linearRampToValueAtTime(0, now + fade / 1000)
      window.setTimeout(() => teardown(voice), fade + 60)
    } else {
      teardown(voice)
    }
  }

  setPaused(emitterId: string, paused: boolean): void {
    const voice = this.voices.get(emitterId)
    if (!voice?.element) return
    if (paused) voice.element.pause()
    else void voice.element.play().catch(() => undefined)
  }

  isPlaying(emitterId: string): boolean {
    return this.voices.has(emitterId)
  }

  playingEmitterIds(): string[] {
    return [...this.voices.keys()]
  }

  /** Applies a frame's worth of decisions to the graph. */
  applyMix(decision: MixDecision, smoothingSeconds = 0.06): void {
    if (!this.context) return
    const now = this.context.currentTime

    for (const [busId, gainNode] of this.buses) {
      const target = decision.busGains[busId]
      if (target !== undefined) gainNode.gain.setTargetAtTime(target, now, smoothingSeconds)
    }

    for (const voice of decision.voices) {
      this.applyVoice(voice, now, smoothingSeconds)
    }
  }

  private applyVoice(decision: VoiceDecision, now: number, smoothing: number): void {
    const voice = this.voices.get(decision.emitterId)
    if (!voice) return

    // A culled voice is silenced but keeps running, so it is exactly where it
    // should be if the listener turns back towards it.
    const target = decision.audible ? decision.gain : 0
    voice.gain.gain.setTargetAtTime(target, now, smoothing)

    const blend = decision.spatialization === 'nonDiegetic' ? 1 : decision.ambientBlend
    voice.pannedGain.gain.setTargetAtTime(1 - blend, now, smoothing)
    voice.directGain.gain.setTargetAtTime(blend, now, smoothing)

    if (decision.spatialization !== 'nonDiegetic') {
      const radians = (decision.azimuthDeg * Math.PI) / 180
      // Unit distance on the computed bearing: the panner supplies direction,
      // never level.
      voice.panner.positionX.setTargetAtTime(Math.sin(radians), now, smoothing)
      voice.panner.positionZ.setTargetAtTime(-Math.cos(radians), now, smoothing)
    }

    if (voice.source instanceof AudioBufferSourceNode) {
      voice.source.playbackRate.setTargetAtTime(decision.playbackRate, now, smoothing)
    } else if (voice.element) {
      voice.element.playbackRate = decision.playbackRate
    }
  }

  setMasterGain(linear: number): void {
    if (!this.master || !this.context) return
    this.master.gain.setTargetAtTime(linear, this.context.currentTime, 0.05)
  }

  // There is deliberately no setBusTrim here. Bus levels are recomputed from
  // the mix decision on every frame, so a gain written straight onto a node is
  // erased within one tick; trims belong to the session runtime, which feeds
  // them into that decision.

  /** Forgets a transient emitter once it has been swept up. */
  unregisterEmitter(emitterId: string): void {
    this.emitters.delete(emitterId)
    this.playlistCursor.delete(emitterId)
  }

  stopAll(): void {
    for (const emitterId of [...this.voices.keys()]) this.stop(emitterId, 0)
  }

  async dispose(): Promise<void> {
    this.stopAll()
    this.buses.clear()
    this.assets.clear()
    this.seamlessBuffers.clear()
    for (const url of this.objectUrls.splice(0)) URL.revokeObjectURL(url)
    await this.context?.close()
    this.context = undefined
  }

  /**
   * Returns a copy of the buffer whose tail is crossfaded into its head, so it
   * can be looped without a click. Equal power, so the seam does not dip.
   *
   * Done to the audio once rather than by overlapping two sources every lap:
   * no scheduler to keep ahead of, and nothing to go wrong when the tab is
   * throttled.
   */
  private seamless(assetId: string, buffer: AudioBuffer, crossfadeMs: number): AudioBuffer {
    const key = `${assetId}:${crossfadeMs}`
    const cached = this.seamlessBuffers.get(key)
    if (cached) return cached
    if (!this.context) return buffer

    const fade = Math.min(
      Math.floor((crossfadeMs / 1000) * buffer.sampleRate),
      Math.floor(buffer.length / 2),
    )
    if (fade <= 0) return buffer

    const length = buffer.length - fade
    const out = this.context.createBuffer(buffer.numberOfChannels, length, buffer.sampleRate)

    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const from = buffer.getChannelData(channel)
      const to = out.getChannelData(channel)
      to.set(from.subarray(0, length))
      for (let i = 0; i < fade; i++) {
        const t = i / fade
        to[i] = to[i] * Math.sqrt(t) + from[length + i] * Math.sqrt(1 - t)
      }
    }

    this.seamlessBuffers.set(key, out)
    return out
  }

  /**
   * Chooses which take to play. Random containers exist so a bird does not
   * play the identical sample every ninety seconds and stop sounding like a
   * bird, so the choice happens per start rather than once.
   */
  private pickAsset(emitterId: string, source: SoundSource): string | undefined {
    if (source.kind === 'asset') return source.assetId

    if (source.kind === 'playlist') {
      const index = this.playlistCursor.get(emitterId) ?? 0
      const next =
        source.order === 'shuffle'
          ? Math.floor(Math.random() * source.assetIds.length)
          : index % source.assetIds.length
      this.playlistCursor.set(emitterId, index + 1)
      return source.assetIds[next]
    }

    const totalWeight = source.variants.reduce((sum, variant) => sum + variant.weight, 0)
    let roll = Math.random() * totalWeight
    for (const variant of source.variants) {
      roll -= variant.weight
      if (roll <= 0) return variant.assetId
    }
    return source.variants[0]?.assetId
  }
}

function teardown(voice: Voice): void {
  try {
    if (voice.source instanceof AudioBufferSourceNode) {
      voice.source.onended = null
      voice.source.stop()
    } else if (voice.element) {
      voice.element.pause()
      voice.element.src = ''
    }
  } catch {
    // Stopping something already stopped is not a problem worth reporting.
  }
  voice.envelope.disconnect()
  voice.gain.disconnect()
  voice.pannedGain.disconnect()
  voice.directGain.disconnect()
  voice.panner.disconnect()
}

/** Audio requests carry the same token as everything else. */
function authHeader(): Record<string, string> {
  const token = localStorage.getItem('sw.accessToken')
  return token ? { authorization: `Bearer ${token}` } : {}
}
